/**
 * 정산 변환기 훅 — 레거시 `useSettlementInfoGenerator.js` 포팅.
 * 서버 API 없이 브라우저에서 xls/xlsx를 파싱·미리보기·멀티시트 내보내기한다.
 */
import {
  parseSettlementFiles,
  SETTLEMENT_EXPORT_HEADER_MAP,
  type SettlementFileStatus,
  type SettlementParseSummary,
  type SettlementRow,
} from '@/pages/settlements/settlementSheetParser';
import { exportSheetsToXlsx } from '@/shared/utils/xlsxExport';
import { useState } from 'react';

export interface SelectedSettlementFile {
  id: string;
  name: string;
  size: number;
  file: File;
}

const emptySummary: SettlementParseSummary = {
  totalFiles: 0,
  parsedFiles: 0,
  unsupportedFiles: 0,
  unmatchedFiles: 0,
  emptyFiles: 0,
  extractedRows: 0,
  pharmaceuticalCompanies: 0,
  counterparties: 0,
};

const fileDedupeKey = (file: File) =>
  `${file.name}-${file.size}-${file.lastModified}`;

export const useSettlementInfoGenerator = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [summary, setSummary] = useState<SettlementParseSummary>(emptySummary);
  const [rows, setRows] = useState<SettlementRow[]>([]);
  const [files, setFiles] = useState<SettlementFileStatus[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<SelectedSettlementFile[]>(
    [],
  );
  const [errorMessage, setErrorMessage] = useState('');

  const clearPreview = () => {
    setSelectedFiles([]);
    setSummary(emptySummary);
    setRows([]);
    setFiles([]);
    setErrorMessage('');
  };

  const addFilesToProcess = (inputFiles: File[] = []) => {
    setSelectedFiles((currentFiles) => {
      const nextFiles = [...currentFiles];
      const existingKeys = new Set(
        currentFiles.map((item) => fileDedupeKey(item.file)),
      );

      inputFiles.forEach((file) => {
        if (!file) return;
        const dedupeKey = fileDedupeKey(file);
        if (existingKeys.has(dedupeKey)) return;

        existingKeys.add(dedupeKey);
        nextFiles.push({
          id: `${dedupeKey}-${nextFiles.length}`,
          name: file.name,
          size: file.size,
          file,
        });
      });

      return nextFiles;
    });
    setErrorMessage('');
  };

  const removeFileToProcess = (fileId: string) => {
    setSelectedFiles((currentFiles) =>
      currentFiles.filter((item) => item.id !== fileId),
    );
  };

  const generatePreview = async (
    inputFiles: SelectedSettlementFile[] = selectedFiles,
  ) => {
    if (!inputFiles.length) {
      clearPreview();
      setErrorMessage('정산 파일을 먼저 선택해 주세요.');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      const preview = await parseSettlementFiles(
        inputFiles.map((item) => item.file),
      );
      setSummary(preview.summary);
      setRows(preview.rows ?? []);
      setFiles(preview.files ?? []);
    } catch (error) {
      console.error(error);
      clearPreview();
      setErrorMessage(
        error instanceof Error ? error.message : '정산 정보 생성 실패',
      );
    } finally {
      setIsLoading(false);
    }
  };

  /** 전체내역 + 제약사명별 시트로 내보내기 (레거시 exportPreview) */
  const exportPreview = () => {
    if (rows.length === 0) return;

    const timestamp = new Date()
      .toISOString()
      .slice(0, 19)
      .replace(/[:T]/g, '-');

    const groupedRows = rows.reduce<Record<string, SettlementRow[]>>(
      (acc, row) => {
        const groupKey = row.pharmaceuticalCompanyName || '미분류';
        if (!acc[groupKey]) {
          acc[groupKey] = [];
        }
        acc[groupKey].push(row);
        return acc;
      },
      {},
    );

    const sheets = [
      {
        sheetName: '전체내역',
        data: rows as unknown as Record<string, unknown>[],
      },
      ...Object.entries(groupedRows).map(([sheetName, data]) => ({
        sheetName,
        data: data as unknown as Record<string, unknown>[],
      })),
    ];

    exportSheetsToXlsx(
      sheets,
      `정산정보생성기_${timestamp}`,
      SETTLEMENT_EXPORT_HEADER_MAP,
    );
  };

  return {
    isLoading,
    summary,
    rows,
    files,
    selectedFiles,
    errorMessage,
    addFilesToProcess,
    removeFileToProcess,
    generatePreview,
    exportPreview,
    clearPreview,
  };
};
